import { TranslocoPipe } from '@jsverse/transloco';
import { Component, inject } from '@angular/core';
import { LucideCircleCheck, LucideInfo, LucideOctagonAlert, LucideTriangleAlert, LucideX } from '@lucide/angular';
import { ToastService } from '../../../core/notifications/toast.service';

@Component({
  selector: 'app-toast-host',
  standalone: true,
  imports: [TranslocoPipe, LucideCircleCheck, LucideInfo, LucideOctagonAlert, LucideTriangleAlert, LucideX],
  template: `
    @for (toast of toastService.toasts(); track toast.id) {
      <article
        class="toast"
        [class.info]="toast.severity === 'info'"
        [class.success]="toast.severity === 'success'"
        [class.warning]="toast.severity === 'warning'"
        [class.error]="toast.severity === 'error'"
        [attr.role]="toast.severity === 'warning' || toast.severity === 'error' ? 'alert' : 'status'"
      >
        <span class="toast__icon" aria-hidden="true">
          @switch (toast.severity) {
            @case ('success') { <svg lucideCircleCheck size="18"></svg> }
            @case ('warning') { <svg lucideTriangleAlert size="18"></svg> }
            @case ('error') { <svg lucideOctagonAlert size="18"></svg> }
            @default { <svg lucideInfo size="18"></svg> }
          }
        </span>
        <div class="toast__copy">
          <strong>{{ toast.title }}</strong>
          <span>{{ toast.message }}</span>
        </div>
        <button type="button" [attr.aria-label]="'core.closeNotification' | transloco" (click)="toastService.dismiss(toast.id)">
          <svg lucideX size="16" aria-hidden="true"></svg>
        </button>
        <span class="toast__timer" [style.animation-duration.ms]="toast.durationMs"></span>
      </article>
    }
  `,
  styles: [`
    :host {
      bottom: var(--space-5);
      display: grid;
      gap: var(--space-3);
      pointer-events: none;
      position: fixed;
      right: var(--space-5);
      width: min(28rem, calc(100vw - 2rem));
      z-index: 1000;
    }

    /* Keep toasts above the sliding page during a route View Transition (see app-shell). */
    :host-context(:root[data-nav-direction]) {
      view-transition-name: route-toasts;
    }

    .toast {
      --toast-color: var(--color-info);
      align-items: center;
      animation: toast-in var(--motion-medium) var(--ease-out) both;
      background: color-mix(in srgb, var(--toast-color) 6%, var(--color-bg-elevated));
      border: 1px solid color-mix(in srgb, var(--toast-color) 28%, var(--color-border-subtle));
      border-radius: var(--radius-card);
      box-shadow: var(--shadow-overlay);
      color: var(--color-text-primary);
      display: grid;
      gap: var(--space-3);
      grid-template-columns: auto minmax(0, 1fr) auto;
      overflow: hidden;
      padding: var(--space-3) var(--space-4);
      pointer-events: auto;
      position: relative;
    }

    @keyframes toast-in {
      from { opacity: 0; transform: translateY(10px) scale(0.98); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }

    .toast::before {
      background: var(--toast-color);
      content: '';
      inset: 0 auto 0 0;
      opacity: 0.8;
      position: absolute;
      width: 0.25rem;
    }

    .toast.info {
      --toast-color: var(--color-info);
    }

    .toast.success {
      --toast-color: var(--color-success);
    }

    .toast.warning {
      --toast-color: var(--color-warning);
    }

    .toast.error {
      --toast-color: var(--color-danger);
    }

    .toast__icon {
      align-items: center;
      background: color-mix(in srgb, var(--toast-color) 12%, transparent);
      border-radius: var(--radius-md);
      color: var(--toast-color);
      display: inline-flex;
      height: 2.35rem;
      justify-content: center;
      width: 2.35rem;
    }

    .toast__copy {
      display: grid;
      gap: 0.1rem;
      min-width: 0;
    }

    strong {
      color: var(--color-text-primary);
      font-weight: 900;
      line-height: 1.25;
    }

    .toast__copy span {
      color: var(--color-text-secondary);
      font-size: var(--font-size-caption);
      font-weight: 700;
      line-height: 1.35;
    }

    button {
      align-items: center;
      background: transparent;
      border: 0;
      border-radius: var(--radius-md);
      color: color-mix(in srgb, var(--toast-color) 82%, var(--color-text-secondary));
      cursor: pointer;
      display: inline-flex;
      height: 2.35rem;
      justify-content: center;
      line-height: 1;
      padding: 0;
      transition: background var(--motion-fast) ease, color var(--motion-fast) ease;
      width: 2.35rem;
    }

    button:hover {
      background: color-mix(in srgb, var(--toast-color) 12%, transparent);
      color: var(--toast-color);
    }
    button:focus-visible {
      outline: var(--focus-ring);
      outline-offset: 2px;
    }

    .toast__timer {
      animation: toast-life linear forwards;
      background: color-mix(in srgb, var(--toast-color) 78%, transparent);
      bottom: 0;
      height: 0.25rem;
      left: 0;
      position: absolute;
      right: 0;
      transform-origin: left center;
    }

    @keyframes toast-life {
      from {
        transform: scaleX(1);
      }
      to {
        transform: scaleX(0);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .toast { animation: none; }
    }

    @media (max-width: 700px) {
      :host {
        bottom: 5.25rem;
        left: var(--space-3);
        right: var(--space-3);
        width: auto;
      }

      .toast {
        align-items: start;
        padding: var(--space-3);
      }
    }
  `],
})
export class AppToastHostComponent {
  readonly toastService = inject(ToastService);
}
